import { Inject, Injectable } from '@angular/core';
import { State, Action, StateContext, Selector } from '@ngxs/store';

/**
 * @title SettingsState
 * @since 1.0.0
 * 
 * @description This state manages the application settings, providing a centralized store 
 * for configuration options.
 * 
 * Current Settings
 * - Theme Settings:
 *   -- darkMode: A boolean indicating whether the application is in dark mode. Defaults to `false` (light mode).
 */


export class ToggleTheme {
    static readonly type = '[Settings] Toggle Theme';
}

export interface SettingsStateModel {
    theme: {
        darkMode: boolean;
    };
}

@State<SettingsStateModel>({
    name: 'settings',
    defaults: {
        theme: {
            darkMode: false
        }
    }
})
@Injectable()
export class SettingsState {

    /**
     * This selector retrieves the current dark mode setting from the state.
     * @param state The current state of the SettingsState.
     * @returns {boolean} The current dark mode setting.
     * @since 1.0.0
     */
    @Selector()
    static isDarkMode(state: SettingsStateModel): boolean {
        return state.theme.darkMode;
    }

    /**
     * This action toggles the dark mode setting in the application state.
     * @since 1.0.0
     */
    @Action(ToggleTheme)
    toggleTheme(ctx: StateContext<SettingsStateModel>) {
        const state = ctx.getState();
        ctx.setState({
            ...state,
            theme: {
                darkMode: !state.theme.darkMode
            }
        });
    }
}